import { env } from '../lib/env.js';

/**
 * ApiError is thrown by the request helper whenever the server
 * returned a non 2xx response. It exposes the canonical envelope
 * fields so consumers can branch on `code` (stable) rather than
 * `message` (human).
 */
export class ApiError extends Error {
  constructor({ code, message, statusCode, details, requestId }) {
    super(message || 'Request failed');
    this.name = 'ApiError';
    this.code = code || 'UNKNOWN';
    this.statusCode = statusCode || 0;
    this.details = details;
    this.requestId = requestId || null;
  }
}

let refreshInFlight = null;

async function doRefresh() {
  if (!refreshInFlight) {
    refreshInFlight = fetch(`${env.apiBase}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json' }
    })
      .then((r) => r.ok)
      .catch(() => false)
      .finally(() => {
        // Release the lock on the next tick so concurrent waiters all
        // see the resolved promise but the next call can refresh anew.
        setTimeout(() => { refreshInFlight = null; }, 0);
      });
  }
  return refreshInFlight;
}

/**
 * Low level request helper.
 *
 * Always sends cookies (httpOnly access + refresh tokens). Parses the
 * canonical `{ success, data, error, request_id }` envelope and
 * returns `data` directly on success. Throws `ApiError` on failure.
 *
 * If the response is 401 with TOKEN_EXPIRED, attempts a single
 * silent refresh and retries the original request. Anything else
 * surfaces immediately.
 */
export async function request(path, options = {}) {
  const url = path.startsWith('http') ? path : `${env.apiBase}${path}`;
  const { method = 'GET', body, headers = {}, signal, raw = false, query } = options;

  const finalUrl = appendQuery(url, query);
  const init = {
    method,
    credentials: 'include',
    signal,
    headers: {
      Accept: 'application/json',
      ...(body && !(body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
      ...headers
    }
  };
  if (body !== undefined && body !== null) {
    init.body = body instanceof FormData ? body : JSON.stringify(body);
  }

  let response = await fetch(finalUrl, init);

  if (response.status === 401 && !options._retried) {
    const parsed = await safeJson(response);
    const code = parsed && parsed.error && parsed.error.code;
    if (code === 'TOKEN_EXPIRED' || code === 'UNAUTHENTICATED') {
      const refreshed = await doRefresh();
      if (refreshed) {
        return request(path, { ...options, _retried: true });
      }
    }
    // Fall through with the parsed body so we throw a clean ApiError.
    throw buildApiError(parsed, response.status);
  }

  if (raw) return response;

  const payload = await safeJson(response);
  if (!response.ok) throw buildApiError(payload, response.status);
  return payload && Object.prototype.hasOwnProperty.call(payload, 'data')
    ? payload.data
    : payload;
}

function appendQuery(url, query) {
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      for (const item of v) params.append(k, String(item));
    } else if (v instanceof Date) {
      params.append(k, v.toISOString());
    } else {
      params.append(k, String(v));
    }
  }
  const qs = params.toString();
  if (!qs) return url;
  return url.includes('?') ? `${url}&${qs}` : `${url}?${qs}`;
}

async function safeJson(response) {
  const ct = response.headers.get('content-type') || '';
  if (!ct.toLowerCase().includes('json')) return null;
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function buildApiError(payload, statusCode) {
  const err = (payload && payload.error) || {};
  return new ApiError({
    code: err.code || 'HTTP_ERROR',
    message: err.message || `HTTP ${statusCode}`,
    statusCode,
    details: err.details,
    requestId: payload && payload.request_id
  });
}

export const api = {
  get: (path, opts) => request(path, { ...opts, method: 'GET' }),
  post: (path, body, opts) => request(path, { ...opts, method: 'POST', body }),
  put: (path, body, opts) => request(path, { ...opts, method: 'PUT', body }),
  patch: (path, body, opts) => request(path, { ...opts, method: 'PATCH', body }),
  delete: (path, opts) => request(path, { ...opts, method: 'DELETE' })
};
